import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
} from 'date-fns';
import { th } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';

import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface CalendarProps {
  className?: string;
  selected?: Date;
  onSelect?: (date: Date) => void;
  locale?: Locale;
}

function Calendar({
  className,
  selected,
  onSelect,
  locale = th,
}: CalendarProps) {
  const [currentMonth, setCurrentMonth] = React.useState(
    selected || new Date()
  );

  const days = React.useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth));
    const end = endOfWeek(endOfMonth(currentMonth));
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const handlePreviousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const weekDays = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.'];

  return (
    <div className={cn('p-3', className)}>
      <div className='flex items-center justify-between mb-2'>
        <div className='text-sm font-medium'>
          {format(currentMonth, 'MMMM yyyy', { locale })}
        </div>
        <div className='flex gap-1'>
          <button
            onClick={handlePreviousMonth}
            className={cn(
              buttonVariants({ variant: 'outline' }),
              'h-7 w-7 p-0'
            )}
          >
            <ChevronLeft className='h-4 w-4' />
          </button>
          <button
            onClick={handleNextMonth}
            className={cn(
              buttonVariants({ variant: 'outline' }),
              'h-7 w-7 p-0'
            )}
          >
            <ChevronRight className='h-4 w-4' />
          </button>
        </div>
      </div>
      <div className='grid grid-cols-7 gap-1 text-center mb-1'>
        {weekDays.map(day => (
          <div
            key={day}
            className='text-[0.8rem] text-muted-foreground font-medium'
          >
            {day}
          </div>
        ))}
      </div>
      <div className='grid grid-cols-7 gap-1'>
        {days.map((day, idx) => {
          const isSelected = selected && isSameDay(day, selected);
          const isCurrentMonth = isSameMonth(day, currentMonth);

          return (
            <button
              key={idx}
              onClick={() => onSelect?.(day)}
              className={cn(
                'h-8 w-8 text-sm p-0 font-normal aria-selected:opacity-100 transition-colors rounded-md',
                !isCurrentMonth && 'text-muted-foreground opacity-50',
                isSelected &&
                  'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
                !isSelected &&
                  isCurrentMonth &&
                  'hover:bg-accent hover:text-accent-foreground'
              )}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
